import React, { useState, useRef, useEffect } from 'react';
import {
    List,
    Datagrid,
    TextField,
    TopToolbar,
    CreateButton,
    ExportButton,
    useRecordContext,
    NumberField,
    Link,
    BooleanField,
    useGetOne,
    Filter,
    TextInput,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { LoadingSection } from '../../common/LoadingSection';
import { useParams } from 'react-router';
import Button from '@mui/material/Button';
import {
    ContentCopy,
    DocumentScanner,
    Edit,
    FormatListNumbered,
    HourglassBottom,
    Close,
    Grade,
    FileUpload,
} from '@mui/icons-material';
import SectionsLinkField from './SectionsLinkField';
import { NoDataSection } from '../../common/NoDataSection';
import QuestionResponsesLinkField from './QuestionResponsesLinkField';
import { format } from 'date-fns';
import { Tooltip, Modal } from '@mui/material';
import StatusTags from '../../common/StatusTags';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ConfirmDeleteButton from '../../common/ConfirmDeleteButton';
import { ChevronRightIcon } from 'lucide-react';
import QuestionPaper from '../../common/question-paper/QuestionPaperView';
import CommonModal from '../../common/ui/CommonModal';
import BulkUploadCreate from '../bulk-uploads/BulkUploadCreate';

const ListActions = ({ testSeriesId }: { testSeriesId: string }) => {
    const navigate = useNavigate();
    return (
        <TopToolbar>
            <CreateButton
                onClick={e => {
                    e.preventDefault();
                    navigate(`/test-series/${testSeriesId}/tests/create`);
                }}
            />
            <ExportButton />
        </TopToolbar>
    );
};

const CloneTest = (props: any) => {
    const record = useRecordContext();
    const { testSeriesId } = useParams();
    const keysToCopy = [
        'image',
        'name',
        'slug',
        'status',
        'type',
        'testSeriesId',
        'startTime',
        'endTime',
        'maxDurationInMinutes',
    ];
    const newRecord: any = {};
    keysToCopy.forEach(b => {
        newRecord[b] = record[b];
    });
    const navigate = useNavigate();
    return (
        <Tooltip title="">
            <button
                className="w-full flex flex-row  pl-2 py-2 text-sm font-semibold uppercase"
                onClick={() => {
                    props.onActionClick?.();
                    navigate(
                        `/test-series/${testSeriesId}/tests/create?source=${encodeURIComponent(
                            JSON.stringify(newRecord),
                        )}`,
                    );
                }}
            >
                <ContentCopy />
                {props.isTagShow && <p className="pl-3 ">Clone</p>}
            </button>
        </Tooltip>
    );
};

const EditButton = ({ isTagShow, onActionClick }: any) => {
    const navigate = useNavigate();
    const record = useRecordContext();
    return (
        <Tooltip title="">
            <button
                className="w-full text-md uppercase font-semibold  flex justify-start items-center gap-2 text-left px-2 py-1"
                onClick={() => {
                    onActionClick?.();
                    navigate(`/test-series/${record?.testSeriesId}/tests/${record.id}/edit`);
                }}
            >
                <Edit fontSize="medium" />
                {isTagShow && 'Edit'}
            </button>
        </Tooltip>
    );
};

const SectionButtonVisibility = () => {
    const record: any = useRecordContext<any>();
    if (record.category == 'OBJECTIVE') {
        return <SectionsLinkField />;
    } else return <div>-</div>;
};

const TestFilters = (props: any) => {
    return (
        <Filter {...props}>
            <TextInput
                variant={'outlined'}
                label="Name"
                source="name"
                alwaysOn={true}
                defaultValue=""
            />
        </Filter>
    );
};

const QuestionPaperButton = ({ onClick }: { onClick: () => void }) => {
    return (
        <Button onClick={onClick}>
            <DocumentScanner fontSize="large" className="pr-2" /> View Questions
        </Button>
    );
};

let currentCloseFunction: (() => void) | null = null;

const Actions = () => {
    const [showActions, setShowActions] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [bulkUploadModal, setBulkUploadModal] = useState(false);
    const record = useRecordContext();

    useEffect(() => {
        const handleClickOutside = () => {
            setShowActions(false);
        };

        if (showActions) {
            if (currentCloseFunction && currentCloseFunction !== handleClickOutside) {
                currentCloseFunction();
            }
            currentCloseFunction = handleClickOutside;

            setTimeout(() => {
                document.addEventListener('click', handleClickOutside);
            }, 0);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
            if (currentCloseFunction === handleClickOutside) {
                currentCloseFunction = null;
            }
        };
    }, [showActions]);

    const handleButtonClick = (e: any) => {
        e.stopPropagation();
        setShowActions(prev => !prev);
    };

    const preventClose = (e: any) => {
        e.stopPropagation();
    };

    const handleOpenModal = () => {
        setShowModal(true);
        setShowActions(false);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    return (
        <div className="relative inline-block">
            <button onClick={handleButtonClick} className="p-2 rounded-full hover:bg-gray-200">
                <MoreVertIcon />
            </button>

            {showActions && (
                <div
                    className="absolute text-md uppercase right-0 mt-2 w-60 bg-white shadow-lg rounded-lg p-2 flex flex-col justify-start text-start gap-2 z-10"
                    onClick={preventClose}
                >
                    <div className="hover:bg-blue-50 w-full">
                        <QuestionPaperButton onClick={handleOpenModal} />
                    </div>
                    <div className=" w-full gap-2 flex justify-start text-[#4f3cc9] text-md  cursor-pointer hover:bg-blue-50">
                        <CloneTest isTagShow={true} onActionClick={() => setShowActions(false)} />
                    </div>
                    <div className="flex  gap-2 justify-start text-[#4f3cc9] text-md cursor-pointer hover:bg-blue-50">
                        <EditButton isTagShow={true} onActionClick={() => setShowActions(false)} />
                    </div>
                    <div className="flex w-full gap-2 justify-start text-[#4f3cc9] text-md items-center cursor-pointer hover:bg-blue-50">
                        <ConfirmDeleteButton setShowActions={setShowActions} />
                    </div>
                    <div className="flex w-full font-semibold p-2  gap-2 justify-start text-[#4f3cc9] text-md items-center cursor-pointer hover:bg-blue-50">
                        <button
                            onClick={() => setBulkUploadModal(true)}
                            className="flex items-center uppercase gap-2"
                        >
                            <FileUpload fontSize="medium" /> Bulk Upload
                        </button>
                    </div>
                </div>
            )}

            <Modal
                open={showModal}
                onClose={handleCloseModal}
                aria-labelledby="question-paper-modal"
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <div className="bg-white rounded-lg shadow-2xl min-w-[75vw] max-w-[80vw] min-h-[90vh] max-h-[95vh] relative flex flex-col">
                    <div className="flex-1 overflow-auto ">
                        {record && <QuestionPaper testId={record.id} />}
                    </div>
                    <div className="sticky bottom-0 z-40 p-2 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-t flex justify-end">
                        <Button variant="contained" onClick={handleCloseModal} aria-label="Close">
                            Close <Close />
                        </Button>
                    </div>
                </div>
            </Modal>
            <CommonModal
                title="Questions Bulk Upload"
                open={bulkUploadModal}
                setOpen={setBulkUploadModal}
            >
                <BulkUploadCreate selectedType="TEST_QUESTION_UPLOAD" />
            </CommonModal>
        </div>
    );
};

Actions.defaultProps = {
    label: 'Actions',
};

export const TestMeta = () => {
    const record = useRecordContext();
    return (
        <div className={'flex justify-start items-center space-x-2'}>
            {
                <div className={'w-14 text-center'}>
                    {record.type === 'LIVE' && (
                        <div className={'text-xs px-1 py-0.5 rounded-md bg-red-500 text-white'}>
                            Live
                        </div>
                    )}
                    {record.type === 'EXERCISE' && (
                        <div className={'text-xs px-1 py-0.5 rounded-md bg-amber-500 text-white'}>
                            Exercise
                        </div>
                    )}
                    {record.type === 'NORMAL' && (
                        <div className={'text-xs px-1 py-0.5 rounded-md bg-blue-500 text-white'}>
                            Normal
                        </div>
                    )}

                    {record.type === 'MOCK' && (
                        <div className={'text-xs px-1 py-0.5 rounded-md bg-purple-500 text-white'}>
                            Mock
                        </div>
                    )}
                </div>
            }
            <div className={'flex flex-col '}>
                <div className={'flex space-x-1 items-center'}>
                    <FormatListNumbered style={{ fontSize: '16px' }} />
                    <div className={'whitespace-nowrap'}>{record.totalQuestions} questions</div>
                </div>
                <div className={'flex space-x-1 items-center'}>
                    <FormatListNumbered style={{ fontSize: '16px' }} />
                    {record.totalMarks} marks
                </div>
                <div className={'flex space-x-1 items-center'}>
                    <HourglassBottom style={{ fontSize: '16px' }} />
                    {record.maxDurationInMinutes} min
                </div>
            </div>
        </div>
    );
};
TestMeta.defaultProps = {
    label: 'Metadata',
};

export const FormattedStartEndTime = () => {
    const record = useRecordContext<any>();
    return record ? (
        <div className={'flex flex-col text-sm space-y-1'}>
            {record.startTime && (
                <div className={'flex items-center space-x-2'}>
                    <div
                        className={
                            'w-4 h-4 flex flex-col items-center justify-center rounded-full bg-blue-200'
                        }
                    >
                        S
                    </div>
                    <div className={'whitespace-nowrap'}>
                        {format(new Date(record.startTime), 'dd/MM/yyyy hh:mm a')}
                    </div>
                </div>
            )}
            {record.endTime && (
                <div className={'flex items-center  space-x-2'}>
                    <div
                        className={
                            'w-4 h-4 flex flex-col items-center justify-center rounded-full bg-red-200'
                        }
                    >
                        E
                    </div>
                    <div className={'whitespace-nowrap'}>
                        {format(new Date(record.endTime), 'dd/MM/yyyy hh:mm a')}
                    </div>
                </div>
            )}
        </div>
    ) : null;
};

FormattedStartEndTime.defaultProps = {
    source: 'startTime',
    label: 'Start - End',
    sortable: false,
};

const TestList = () => {
    const { testSeriesId } = useParams();
    const navigate = useNavigate();
    if (!testSeriesId) {
        return <LoadingSection />;
    }
    return (
        <List
            title={<TestTitle />}
            sort={{ field: 'created_at', order: 'DESC' }}
            empty={
                <NoDataSection
                    resource={'tests'}
                    onCreate={() => {
                        navigate(`/test-series/${testSeriesId}/tests/create`);
                    }}
                />
            }
            resource={'tests'}
            actions={<ListActions testSeriesId={testSeriesId || ''} />}
            filters={<TestFilters />}
            filterDefaultValues={{ testSeriesId: testSeriesId }}
            queryOptions={{
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
            }}
        >
            <Datagrid bulkActionButtons={false}>
                {/*<ThumbnailField source="image"/>*/}
                <TextField source="name" sortable={false} />
                <StatusTags source="status" label={'Status'} sortable={false} />
                <BooleanField source="isDemo" sortable={false} />
                <TextField source="slug" sortable={false} />
                <NumberField source="displayOrder" />
                <TestMeta />
                <FormattedStartEndTime source={'startTime'} sortable={false} />
                <QuestionResponsesLinkField />
                <SectionButtonVisibility />
                <Actions />
            </Datagrid>
        </List>
    );
};

const TestTitle = () => {
    const { testSeriesId } = useParams();
    const { data: testSeries, isLoading } = useGetOne('test-series', { id: testSeriesId });

    if (isLoading) return <span>Loading...</span>;

    return (
        <span className="flex flex-row items-center">
            <Link to={`/test-series`} style={{ textDecoration: 'none', color: 'blue' }}>
                {testSeries?.title}
            </Link>
            <ChevronRightIcon />
            {'Tests'}
        </span>
    );
};
export default TestList;
